import { XYChart } from '@amcharts/amcharts5/xy';
import * as am5 from '@amcharts/amcharts5';
import * as am5core from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { generateMarkers } from './chartUtils';
import dayjs from 'dayjs';
import { formatDate } from './dateHelpers';

export interface ChartConfig {
  name: string;
  lineColor: string;
  lineType: string;
  lineWeight: number;
  markerColor: string;
  markerType: string;
  markerSize: number;
}

export interface DowntimeEntry {
  date: string;
  endDate: string;
  dateLocalMilliseconds: number;
  endDateLocalMilliseconds: number;
  hours: number;
  isActiveDowntime: boolean;
  dateLocal: string;
  endDateLocal: string;
}

export interface HighlightEvent {
  start: number;
  end: number;
  CreatedMilliseconds: number;
  Note: string | null;
  WellEventCreated: string;
}

export function prepareChartContainer(container: HTMLElement | null): boolean {
  if (!(container instanceof HTMLElement)) {
    return false;
  }

  const existingRoot = am5.registry.rootElements.find((r) => r.dom === container);
  if (existingRoot) {
    existingRoot.dispose();
  }
  return true;
}

export function getSortedChartData(chartData: any[], parameterData: any[]) {
  if (!chartData || chartData.length === 0) return [];
  return chartData
    .flatMap((chart: any) => {
      const param = parameterData?.find(
        (param: any) => param.name.trim().toLowerCase() === chart.title.trim().toLowerCase(),
      );
      // Exclude chart if param.selected is explicitly false
      if (param?.selected === false) return [];
      return {
        ...chart,
        displayOrder: Number(param?.displayOrder) || 0,
        selected: param?.selected ?? true,
      };
    })
    .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
}

export const createCursorBullet = (root: am5.Root, markerColor: string) => {
  return am5.Bullet.new(root, {
    sprite: am5.Circle.new(root, {
      radius: 6,
      fill: am5.color('#323F46'),
      stroke: am5.color(markerColor),
      strokeWidth: 4,
      visible: false,
      x: -9999,
      y: -9999,
      layer: 100,
    }),
  });
};

export const customizeAxisLabels = (renderer: am5xy.AxisRenderer, fontSize = 12) => {
  renderer.labels.template.setAll({
    fontFamily: 'Mulish',
    fontSize: fontSize,
    fill: am5.color('#f7f9f9'),
    paddingRight: 15,
    paddingLeft: 15,
    textAlign: 'right',
    centerY: am5.p50,
    centerX: am5.p0,
    inside: false,
    dx: 0,
    width: undefined,
    oversizedBehavior: 'truncate',
  });
};

export const configureXAxisRendererTicks = (xAxisRenderer: am5xy.AxisRendererX) => {
  xAxisRenderer.ticks.template.setAll({
    stroke: am5core.color('#FFFFFF'),
    strokeOpacity: 1,
    visible: true,
    minPosition: 0,
    maxPosition: 1,
  });

  xAxisRenderer.labels.template.setAll({
    location: 0,
  });
};

export const configureYAxisRendererTicks = (yAxisRenderer: am5xy.AxisRendererY, options?: { minPosition?: number }) => {
  yAxisRenderer.ticks.template.setAll({
    stroke: am5core.color('#FFFFFF'),
    strokeOpacity: 1,
    visible: true,
    minPosition: options?.minPosition ?? 0, // Default to 0 if not provided
    maxPosition: 1,
  });
};

export const setupCursor = (
  chart: am5xy.XYChart,
  root: am5.Root,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  color: string,
  strokeWidth: number,
  yAxis?: am5xy.ValueAxis<am5xy.AxisRenderer>,
) => {
  const cursor = chart.set(
    'cursor',
    am5xy.XYCursor.new(root, {
      behavior: 'zoomX',
      xAxis: xAxis,
      yAxis: yAxis,
      snapToSeries: chart.series.values,
      snapToSeriesBy: 'x',
    }),
  );
  cursor.lineX.setAll({ stroke: am5core.color(color), strokeWidth: strokeWidth });
  cursor.lineY.setAll({ stroke: am5core.color(color), strokeWidth: strokeWidth, visible: false });
  return cursor;
};

export const createChartLegend = (chart: am5.Container, root: am5.Root) => {
  const legend = chart.children.push(
    am5.Legend.new(root, {
      x: am5.p50,
      centerX: am5.p50,
      paddingLeft: 80,
      marginTop: 30,
      useDefaultMarker: true,
    }),
  );

  // Style legend text
  legend.labels.template.setAll({
    fill: am5.color('#b8c5cc'),
    fontSize: 12,
    fontFamily: 'Mulish',
    fontWeight: '500',
  });

  // Customize legend markers to be circles
  legend.markers.template.setAll({
    width: 10,
    height: 10,
  });

  legend.markerRectangles.template.setAll({
    cornerRadiusTL: 50,
    cornerRadiusTR: 50,
    cornerRadiusBL: 50,
    cornerRadiusBR: 50,
    strokeWidth: 2,
  });

  legend.markerRectangles.template.adapters.add('fill', (_fill, target) => {
    const dataItem = target.dataItem;
    const series = dataItem?.dataContext as am5xy.LineSeries;
    const isVisible = series?.get('visible');
    const markerColor = (series as any).getPrivate('legendMarkerFill');
    const emptyDataColor = am5.color('#8a8f98');
    const inactiveColor = am5.color('#F7F9F9');

    const hasNoData =
      (series && (!series.data || series.data.length === 0 || series.data.values.length === 0)) || !markerColor;

    if (hasNoData) {
      target.set('fillOpacity', 0);
      target.set('stroke', emptyDataColor);
      return am5.color('rgba(0, 0, 0, 0)');
    } else {
      target.set('fillOpacity', isVisible === false ? 0 : 1);
      target.set('stroke', isVisible === false ? inactiveColor : markerColor);
      return markerColor;
    }
  });
  return legend;
};

function getSetpointLine(thresholds?: {
  setpoint?: number | null;
  setpointMin?: number | null;
  setpointMax?: number | null;
}): { line: string; hasSetpointRange: boolean } {
  const hasSetpointRange = thresholds?.setpointMin != null || thresholds?.setpointMax != null;

  if (!hasSetpointRange) {
    return { line: '', hasSetpointRange: false };
  }

  const spMinValue = thresholds?.setpointMin != null ? `${thresholds.setpointMin} Hz` : '';
  const spMaxValue = thresholds?.setpointMax != null ? `${thresholds.setpointMax} Hz` : '';
  const spMeanValue = thresholds?.setpoint != null ? `${thresholds.setpoint} Hz` : '';

  const line = `\nSetpoint Min: ${spMinValue}  |  Setpoint Max: ${spMaxValue}  |  Setpoint Mean: ${spMeanValue}`;

  return { line, hasSetpointRange };
}

export const createTooltip = (
  text: string,
  root: am5.Root,
  options?: {
    backgroundColor?: string;
    backgroundOpacity?: number;
    strokeColor?: string;
    textColor?: string;
    fontSize?: number;
    fontFamily?: string;
    verticalPosition?: 'top' | 'center';
    chartHeight?: number; // Required if verticalPosition is 'center'
    thresholds?: {
      setpoint?: number | null;
      setpointMin?: number | null;
      setpointMax?: number | null;
    };
    textAlign?: 'left' | 'center' | 'right';
  },
) => {
  const { textAlign = 'left' } = options || {};
  const tooltip = am5.Tooltip.new(root, {
    getFillFromSprite: false,
    getLabelFillFromSprite: false,
    autoTextColor: false,
    opacity: 1,
    layer: 101,
  });

  const { line: setpointLine, hasSetpointRange } = getSetpointLine(options?.thresholds);
  tooltip.label.set('text', `${text}${setpointLine}`);

  // Set background properties
  tooltip.get('background')?.setAll({
    fill: am5core.color(options?.backgroundColor || '#001023'),
    opacity: options?.backgroundOpacity ?? 1,
    strokeWidth: 1,
    stroke: am5core.color(options?.strokeColor || '#001023'),
  });

  // Set text properties
  tooltip.label.setAll({
    fill: am5core.color(options?.textColor || '#FFFFFF'),
    fontFamily: options?.fontFamily || 'Mulish',
    fontSize: options?.fontSize || 14,
    textAlign: textAlign,
  });

  // Adjust vertical position if specified
  if (options?.verticalPosition === 'center' && options.chartHeight) {
    tooltip.adapters.add('y', () => {
      return (options?.chartHeight ?? 0) / 2;
    });
  } else if (options?.verticalPosition === 'top') {
    tooltip.adapters.add('y', () => {
      return hasSetpointRange ? 28 : 20; // Fixed position at the top
    });
  }

  return tooltip;
};

/**
 * Calculate dynamic tooltip position with smart flipping
 * Returns position and orientation based on available space
 */
const calculateTooltipPosition = (
  xCoord: number,
  chartWidth: number,
  tooltipWidth: number,
  offset: number,
): { x: number; dx: number; pointerOrientation: 'left' | 'right' } => {
  const minGap = offset || 20; // Minimum gap between cursor and tooltip
  const padding = 10; // Padding from chart edges
  const buffer = 10; // Additional buffer to be conservative

  // Try positioning on the right first (preferred)
  const rightPosition = xCoord + minGap;
  const spaceOnRight = chartWidth - rightPosition - padding;
  const estimatedTooltipWidth = tooltipWidth || 180; // Conservative estimate
  const requiredSpace = estimatedTooltipWidth + buffer; // Required space with buffer

  // Only flip to left if there's clearly not enough space on right
  if (spaceOnRight >= requiredSpace) {
    // Enough space on right, position tooltip to the right (preferred)
    return {
      x: xCoord + minGap,
      dx: minGap,
      pointerOrientation: 'left',
    };
  } else {
    // Not enough space on right, check if we can position on left
    const leftPosition = xCoord - minGap - estimatedTooltipWidth;
    const spaceOnLeft = leftPosition - padding;

    // Only flip to left if there's enough space on left AND not enough on right
    if (spaceOnLeft >= 0 && spaceOnRight < requiredSpace) {
      // Enough space on left, flip to left
      return {
        x: leftPosition,
        dx: -minGap,
        pointerOrientation: 'right',
      };
    } else {
      // Not enough space on either side, position as close as possible
      // Prefer right side unless we're very close to right edge (85%+)
      if (xCoord > chartWidth * 0.85) {
        // Very close to right edge, position on left with minimal space
        const leftX = Math.max(padding, xCoord - estimatedTooltipWidth - padding);
        return {
          x: leftX,
          dx: leftX - xCoord,
          pointerOrientation: 'right',
        };
      } else {
        // Still prefer right side, just reduce gap if needed
        const maxRightX = chartWidth - estimatedTooltipWidth - padding;
        const rightX = Math.min(maxRightX, xCoord + minGap);
        return {
          x: rightX,
          dx: rightX - xCoord,
          pointerOrientation: 'left',
        };
      }
    }
  }
};

export const setupCursorEvents = (
  cursor: am5xy.XYCursor,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  chart: am5xy.XYChart,
  allSeries: am5xy.LineSeries[],
  bullets: am5.Bullet[],
  chartData: any[],
  parameterData: any[],
  chartType: string,
  options?: {
    customTooltip?: am5.Tooltip;
    title?: string;
    shortUnitOfMeasure?: string;
    showTooltip?: boolean;
    syncCursor?: () => void;
    chartConfig?: any;
    tooltipOffset?: number; // Optional offset to move tooltip to the right
    singleTooltipOnly?: boolean;
  },
) => {
  let isPointerOver = false;
  chart.plotContainer.events.on('pointerover', () => {
    isPointerOver = true;
  });
  chart.plotContainer.events.on('pointerout', () => {
    isPointerOver = false;
  });
  cursor.events.on('cursormoved', () => {
    const cursorX = cursor.getPrivate('positionX');
    if (cursorX === undefined) {
      bullets.forEach((bullet) => bullet.get('sprite')?.set('visible', false));
      if (options?.customTooltip) {
        try {
          if (!options.customTooltip.isDisposed?.()) {
            options.customTooltip.hide();
          }
        } catch (error) {
          console.warn('Error hiding custom tooltip:', error);
        }
      }
      return;
    }

    // --- Zoom-aware calculation ---
    const axisStart = xAxis.get('start');
    const axisEnd = xAxis.get('end');
    if (typeof axisStart !== 'number' || typeof axisEnd !== 'number') {
      bullets.forEach((bullet) => bullet.get('sprite')?.set('visible', false));
      return;
    }
    const axisRange = axisEnd - axisStart;
    const xValue = xAxis.positionToValue(axisStart + cursorX * axisRange);
    if (!isPointerOver) {
      bullets.forEach((bullet) => bullet.get('sprite')?.set('visible', false));
      if (options?.customTooltip) {
        try {
          if (!options.customTooltip.isDisposed?.()) options.customTooltip.hide();
        } catch (e) {
          void e;
        }
      }
      if (options?.syncCursor) options.syncCursor();
      return;
    }

    const visibleSeries: string[] = [];
    allSeries.forEach((series, idx) => {
      if (!series.get('visible')) {
        bullets[idx].get('sprite')?.set('visible', false);
        return;
      }

      let closestDataItem: any = null;
      let minDist = Infinity;
      series.dataItems.forEach((di: any) => {
        const val = di.get('valueX');
        if (val != null) {
          const dist = Math.abs(val - xValue);
          if (dist < minDist) {
            minDist = dist;
            closestDataItem = di;
          }
        }
      });

      // Only show bullet if cursor is close to a real data point
      let showBullet = true;
      const threshold = 60 * 1000; // 1 minute
      showBullet = minDist <= threshold;

      if (closestDataItem && showBullet) {
        const closestX = closestDataItem.get('valueX');
        const closestY = closestDataItem.get('valueY');

        if (typeof closestX === 'number' && typeof closestY === 'number' && !isNaN(closestX) && !isNaN(closestY)) {
          // Use zoomed range for position
          const xPos = (xAxis.valueToPosition(closestX) - axisStart) / axisRange;
          const yAxisObj = series.get('yAxis') as am5xy.ValueAxis<am5xy.AxisRenderer>;

          // Handle different Y-axes (left vs right)
          const yAxisStart = yAxisObj.get('start') || 0;
          const yAxisEnd = yAxisObj.get('end') || 1;
          const yAxisRange = yAxisEnd - yAxisStart;

          // Apply consistent zoom-aware positioning for both left and right Y-axes
          const rawYPos = yAxisObj.valueToPosition(closestY);
          const yPos = (rawYPos - yAxisStart) / yAxisRange;

          const xCoord = chart.plotContainer.width() * xPos;
          const yCoord = chart.plotContainer.height() * (1 - yPos);

          bullets[idx].get('sprite')?.setAll({ x: xCoord, y: yCoord, visible: true });

          // Handle tooltip content based on chart type
          if (options?.customTooltip && options?.title && options?.shortUnitOfMeasure) {
            // COV chart specific tooltip formatting
            if (idx === 0) {
              const bulletColor =
                chartType === 'scatter'
                  ? options?.chartConfig?.markerColor || '#60BFDA'
                  : options?.chartConfig?.chartColor || '#60BFDA';
              visibleSeries.push(
                `[#${bulletColor.replace('#', '')}]●[/] ${options.title}: ${closestY?.toFixed(2)} ${options.shortUnitOfMeasure}`,
              );
            } else if (idx === 1) {
              visibleSeries.push(`[#E94E89]●[/] ${options.title} COV: ${closestY?.toFixed(2)}`);
            } else if (idx === 2) {
              visibleSeries.push(`[#35BB21]●[/] ${options.title} Cumulative COV: ${closestY?.toFixed(2)}`);
            }
          } else {
            // Regular chart tooltip formatting
            const dataset = chartData[idx];
            if (!dataset) return;
            const matchedParam = parameterData.find(
              (param) => param.name.toLowerCase() === dataset.title.toLowerCase(),
            );
            const lineColor = matchedParam?.lineColor || '#60BFDA';
            const markerColor = matchedParam?.markerColor || '#60BFDA';
            const bulletColor = chartType === 'scatter' ? markerColor : lineColor;

            visibleSeries.push(
              `[#${bulletColor.replace('#', '')}]●[/] ${dataset.title}: ${closestY?.toFixed(2)} ${dataset.shortUnitOfMeasure}`,
            );
          }

          const tooltip = series.get('tooltip');
          if (tooltip) {
            const offset = options?.tooltipOffset ?? 0;

            // Only apply dynamic positioning if tooltipOffset is provided (OilGasChart only)
            if (offset > 0) {
              const chartWidth = chart.plotContainer.width();

              // Estimate tooltip width (try to get actual width if available, otherwise use estimate)
              let tooltipWidth = 200; // Default estimate
              if (tooltip.label) {
                const measuredWidth = tooltip.label.width();
                if (measuredWidth > 0) {
                  tooltipWidth = measuredWidth + 40; // Add padding
                }
              }

              // Calculate dynamic position
              const position = calculateTooltipPosition(xCoord, chartWidth, tooltipWidth, offset);

              tooltip.set('pointerOrientation', position.pointerOrientation);
              tooltip.set('dx', position.dx);
              tooltip.set('x', position.x);
            } else {
              // Original behavior for other charts - no positioning changes
              tooltip.set('pointerOrientation', 'left');
              tooltip.set('x', xCoord);
            }
          }
        } else {
          bullets[idx].get('sprite')?.set('visible', false);
        }
      }
    });

    // Handle custom tooltip for COV charts
    if (options?.customTooltip && options?.showTooltip) {
      const tooltipText = `[bold]{valueX.formatDate("MMM d, yyyy hh:mm a")}[/]\n${visibleSeries.join('\n')}`;
      options.customTooltip.label.set('text', tooltipText);

      const offset = options?.tooltipOffset ?? 0;

      // Only apply dynamic positioning if tooltipOffset is provided (OilGasChart only)
      if (offset > 0) {
        // Get cursor position for tooltip positioning
        const cursorX = cursor.getPrivate('positionX');
        if (cursorX !== undefined) {
          const axisStart = xAxis.get('start');
          const axisEnd = xAxis.get('end');
          if (typeof axisStart === 'number' && typeof axisEnd === 'number') {
            const axisRange = axisEnd - axisStart;
            const xPos = (xAxis.valueToPosition(xValue) - axisStart) / axisRange;
            const xCoord = chart.plotContainer.width() * xPos;
            const chartWidth = chart.plotContainer.width();

            // Estimate tooltip width
            let tooltipWidth = 200;
            if (options.customTooltip.label) {
              const measuredWidth = options.customTooltip.label.width();
              if (measuredWidth > 0) {
                tooltipWidth = measuredWidth + 40;
              }
            }

            // Calculate dynamic position
            const position = calculateTooltipPosition(xCoord, chartWidth, tooltipWidth, offset);
            options.customTooltip.set('pointerOrientation', position.pointerOrientation);
            options.customTooltip.set('dx', position.dx);
            options.customTooltip.set('x', position.x);
          }
        }
      } else {
        // Original behavior for other charts
        options.customTooltip.set('pointerOrientation', 'left');
      }
      options.customTooltip.show();
    } else {
      // Apply dynamic tooltip label text to all visible tooltips (regular charts)
      const tooltipText = `[bold]{valueX.formatDate("MMM d, yyyy hh:mm a")}[/]\n${visibleSeries.join('\n')}`;

      // If singleTooltipOnly is requested, show only the nearest series' tooltip
      if (options?.singleTooltipOnly) {
        type SeriesInfo = {
          series: any;
          idx: number;
          minDist: number;
          closestDataItem: any;
          xCoord: number | null;
          labelText: string;
          visible: boolean;
        };

        const seriesInfos: SeriesInfo[] = [];
        allSeries.forEach((series, idx) => {
          let closestDataItem: any = null;
          let minDist = Infinity;
          series.dataItems.forEach((di: any) => {
            const val = di.get('valueX');
            if (val != null) {
              const dist = Math.abs(val - xValue);
              if (dist < minDist) {
                minDist = dist;
                closestDataItem = di;
              }
            }
          });

          let xCoord: number | null = null;
          if (closestDataItem) {
            const axisStart = xAxis.get('start');
            const axisEnd = xAxis.get('end');
            if (typeof axisStart === 'number' && typeof axisEnd === 'number') {
              const axisRange = axisEnd - axisStart;
              const closestX = closestDataItem.get('valueX');
              const xPos = (xAxis.valueToPosition(closestX) - axisStart) / axisRange;
              xCoord = chart.plotContainer.width() * xPos;
            }
          }

          // prepare label text for this series (reuse formatting from above)
          let labelText = '';
          const dataset = chartData[idx];
          if (dataset) {
            const matchedParam = parameterData.find(
              (param) => param.name.toLowerCase() === dataset.title.toLowerCase(),
            );
            const lineColor = matchedParam?.lineColor || '#60BFDA';
            const markerColor = matchedParam?.markerColor || '#60BFDA';
            const bulletColor = chartType === 'scatter' ? markerColor : lineColor;

            labelText = `[#${bulletColor.replace('#', '')}]●[/] ${dataset.title}: ${
              closestDataItem ? closestDataItem.get('valueY')?.toFixed(2) : 'n/a'
            } ${dataset.shortUnitOfMeasure}`;
          }

          seriesInfos.push({
            series,
            idx,
            minDist,
            closestDataItem,
            xCoord,
            labelText,
            visible: !!series.get('visible'),
          });
        });

        const threshold = 60 * 1000; // 1 minute
        const candidates = seriesInfos.filter((s) => s.visible && s.closestDataItem && s.minDist <= threshold);
        candidates.sort((a, b) => a.minDist - b.minDist);
        const best = candidates.length > 0 ? candidates[0] : null;

        allSeries.forEach((s) => {
          try {
            const t = s.get('tooltip');
            if (t) {
              if (typeof t.hide === 'function') t.hide();
              t.set?.('visible', false);
            }
          } catch {
            // ignore
          }
        });

        if (best) {
          const finalTooltipText = `[bold]{valueX.formatDate("MMM d, yyyy hh:mm a")}[/]\n${best.labelText}`;

          if (options?.customTooltip && options?.showTooltip) {
            options.customTooltip.label.set('text', finalTooltipText);
            options.customTooltip.show();
          } else {
            const tooltip = best.series.get('tooltip');
            if (tooltip) {
              tooltip.label.set('text', finalTooltipText);
              tooltip.set?.('pointerOrientation', 'left');
              tooltip.set?.('visible', true);
              try {
                if (typeof tooltip.show === 'function') tooltip.show();
              } catch {
                // ignore
              }
            }
          }
        }
      } else {
        let tooltipPosition: { x: number; dx: number; pointerOrientation: 'left' | 'right' } | null = null;
        const offset = options?.tooltipOffset ?? 0;

        if (offset > 0 && visibleSeries.length > 0) {
          // Get cursor position for tooltip positioning
          const cursorX = cursor.getPrivate('positionX');
          if (cursorX !== undefined) {
            const axisStart = xAxis.get('start');
            const axisEnd = xAxis.get('end');
            if (typeof axisStart === 'number' && typeof axisEnd === 'number') {
              const axisRange = axisEnd - axisStart;
              const xPos = (xAxis.valueToPosition(xValue) - axisStart) / axisRange;
              const xCoord = chart.plotContainer.width() * xPos;
              const chartWidth = chart.plotContainer.width();

              // Estimate tooltip width based on content
              let tooltipWidth = 200; // Default estimate
              // Try to get actual width from first visible tooltip
              const firstVisibleSeries = allSeries.find((s) => s.get('visible'));
              if (firstVisibleSeries) {
                const firstTooltip = firstVisibleSeries.get('tooltip');
                if (firstTooltip && firstTooltip.label) {
                  firstTooltip.label.set('text', tooltipText);
                  const measuredWidth = firstTooltip.label.width();
                  if (measuredWidth > 0) {
                    tooltipWidth = measuredWidth + 40; // Add padding
                  }
                }
              }

              // Calculate dynamic position
              tooltipPosition = calculateTooltipPosition(xCoord, chartWidth, tooltipWidth, offset);
            }
          }
        }

        allSeries.forEach((series) => {
          const tooltip = series.get('tooltip');
          if (tooltip && series.get('visible')) {
            tooltip.label.set('text', tooltipText);

            // Only apply dynamic positioning if tooltipOffset is provided (OilGasChart only)
            if (tooltipPosition) {
              tooltip.set('pointerOrientation', tooltipPosition.pointerOrientation);
              tooltip.set('dx', tooltipPosition.dx);
              // Note: x position is set by amCharts automatically, but dx adjusts it
            } else {
              // Original behavior for other charts - no positioning changes
              tooltip.set('pointerOrientation', 'left');
            }

            tooltip.set('visible', true);
          }
        });
      }
    }

    // Sync cursor with other charts if provided
    if (options?.syncCursor) {
      options.syncCursor();
    }
  });

  // Hide bullets on cursor exit
  cursor.events.on('cursorhidden', () => {
    bullets.forEach((bullet) => bullet.get('sprite')?.set('visible', false));
    if (options?.customTooltip) {
      try {
        if (!options.customTooltip.isDisposed?.()) {
          options.customTooltip.hide();
        }
      } catch (error) {
        console.warn('Error hiding custom tooltip:', error);
      }
    }
  });
};

export function renderDowntimeAndHighlightEvents(
  root: am5.Root,
  xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
  chartViewOptions: any,
  downtimeData: DowntimeEntry[] = [],
  showDowntimeTooltip: boolean,
  highlightEvents: HighlightEvent[] = [],
  isStackedChart?: boolean,
  thresholds?: {
    setpoint?: number | null;
    setpointMin?: number | null;
    setpointMax?: number | null;
  },
) {
  const { hasSetpointRange } = getSetpointLine(thresholds);
  let previousDowntimeData: DowntimeEntry[] = [];
  let previousHighlightEvents: HighlightEvent[] = [];

  const downtimeOverlays: am5.Rectangle[] = [];
  const highlightOverlays: am5.Rectangle[] = [];

  root.events.on('frameended', () => {
    if (!chartViewOptions.showEvents) {
      downtimeOverlays.length = 0;
      highlightOverlays.length = 0;
      return;
    }

    const downData: DowntimeEntry[] = downtimeData ?? [];

    const isDowntimeSame =
      downData.length === previousDowntimeData.length &&
      downData.every((entry, index) => {
        const prev = previousDowntimeData[index];
        return (
          entry.dateLocalMilliseconds === prev.dateLocalMilliseconds &&
          entry.endDateLocalMilliseconds === prev.endDateLocalMilliseconds &&
          entry.hours === prev.hours
        );
      });

    const isHighlightsSame =
      highlightEvents.length === previousHighlightEvents.length &&
      highlightEvents.every((event, index) => {
        const prev = previousHighlightEvents[index];
        return (
          event.CreatedMilliseconds === prev.CreatedMilliseconds &&
          event.Note === prev.Note &&
          event.WellEventCreated === prev.WellEventCreated
        );
      });

    if (isDowntimeSame && isHighlightsSame) return;

    downtimeOverlays.forEach((overlay) => overlay.dispose());
    downtimeOverlays.length = 0;

    highlightOverlays.forEach((overlay) => overlay.dispose());
    highlightOverlays.length = 0;

    previousDowntimeData = [...downData];
    previousHighlightEvents = [...highlightEvents];

    if (downData.length > 0) {
      downData.forEach((downtime: DowntimeEntry) => {
        const rangeDataItem = xAxis.makeDataItem({
          value: dayjs(downtime.dateLocal).valueOf(),
          endValue: dayjs(downtime.endDateLocal).valueOf(),
        });

        const axisRange = xAxis.createAxisRange(rangeDataItem);

        const axisFill = axisRange.get('axisFill');

        if (axisFill) {
          axisFill.setAll({
            fill: am5.color('#FA6020'),
            fillOpacity: 0.6,
            visible: true,
            cursorOverStyle: 'pointer',
          });

          const start = formatDate(dayjs(downtime.dateLocal).toDate(), true);
          const end = formatDate(dayjs(downtime.endDateLocal).toDate(), true);
          const hours = downtime.hours.toFixed(2);

          const tooltipCustom = am5.Tooltip.new(root, {
            labelText: `Downtime (${start} - ${end}) - (${hours} hrs)`,
            height: 36,
            keepTargetHover: true,
          });

          const tooltipBg = am5.RoundedRectangle.new(root, {
            fill: am5.color(0xfca682),
            fillOpacity: 1,
            stroke: am5.color(0x444444),
            strokeWidth: 1,
            cornerRadiusTL: 4,
            cornerRadiusTR: 4,
            cornerRadiusBL: 4,
            cornerRadiusBR: 4,
            layer: 101,
          });

          tooltipCustom.set('background', tooltipBg);

          tooltipCustom.label.setAll({
            fill: am5.color(0x000000),
            fontSize: 14,
            textAlign: 'center',
            layer: 101,
          });

          root.tooltipContainer.children.push(tooltipCustom);

          const tooltipDom = tooltipCustom._root.dom;
          if (tooltipDom) tooltipDom.style.cursor = 'default';

          let hideTimeout: number | null = null;

          axisFill.events.on('pointerover', (ev) => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }

            const point = ev.point;
            const chartWidth = root.container.width();
            const tooltipWidth = tooltipCustom.label.width() + 20;

            let tooltipX = point.x - tooltipWidth / 2;
            if (tooltipX < 0) tooltipX = 0;
            else if (tooltipX + tooltipWidth > chartWidth) tooltipX = chartWidth - tooltipWidth;

            tooltipCustom.setAll({
              x: tooltipX,
              y: isStackedChart ? (hasSetpointRange ? 58 : 40) : 0,
              centerX: 0,
              centerY: 1,
              visible: true,
            });

            if (showDowntimeTooltip) {
              tooltipCustom.show();
            }
          });

          axisFill.events.on('pointerout', () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = window.setTimeout(() => {
              tooltipCustom.hide();
            }, 300);
          });

          tooltipCustom.events.on('pointerover', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });

          tooltipCustom.events.on('pointerout', () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = window.setTimeout(() => {
              tooltipCustom.hide();
            }, 300);
          });

          axisFill.set('y', -20);
        }

        axisRange.get('grid')?.set('visible', false);
      });
    }

    if (highlightEvents.length > 0) {
      const formattedEvents = highlightEvents.map((item) => {
        const startOf5AM: number = dayjs(item.WellEventCreated).startOf('day').add(5, 'hour').valueOf();
        return {
          ...item,
          start: startOf5AM,
          end: Number(startOf5AM) + 5 * 60 * 60 * 1000,
        };
      });
      formattedEvents.forEach((event) => {
        const rangeDataItem = xAxis.makeDataItem({
          value: event.start,
          endValue: event.end,
        });
        const axisRange = xAxis.createAxisRange(rangeDataItem);
        const axisFill = axisRange.get('axisFill');

        if (axisFill) {
          axisFill.setAll({
            fill: am5.color('#FF8F61'),
            fillOpacity: 0.4,
            visible: true,
            cursorOverStyle: 'pointer',
          });

          const formattedDate = formatDate(dayjs(event.WellEventCreated).toDate());
          const tooltip = am5.Tooltip.new(root, {
            labelText: `Well notes: ${event.Note ?? ''} ${formattedDate}`,
            keepTargetHover: true,
          });

          const tooltipBg = am5.RoundedRectangle.new(root, {
            fill: am5.color(0xfca682),
            fillOpacity: 1,
            stroke: am5.color(0x444444),
            strokeWidth: 1,
            cornerRadiusTL: 4,
            cornerRadiusTR: 4,
            cornerRadiusBL: 4,
            cornerRadiusBR: 4,
            layer: 101,
          });

          tooltip.set('background', tooltipBg);

          tooltip.label.setAll({
            fill: am5.color(0x000000),
            fontSize: 14,
            fontWeight: '400',
            textAlign: 'center',
            layer: 101,
          });

          root.tooltipContainer.children.push(tooltip);

          const tooltipDom = tooltip._root.dom;
          if (tooltipDom) tooltipDom.style.cursor = 'default';

          let hideTimeout: number | null = null;

          axisFill.events.on('pointerover', (ev) => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }

            const point = ev.point;
            const chartWidth = root.container.width();
            const tooltipWidth = tooltip.label.width() + 20;

            let tooltipX = point.x - tooltipWidth / 2;
            if (tooltipX < 0) tooltipX = 0;
            else if (tooltipX + tooltipWidth > chartWidth) tooltipX = chartWidth - tooltipWidth;

            tooltip.setAll({
              x: tooltipX,
              y: isStackedChart ? (hasSetpointRange ? 58 : 40) : 0,
              centerX: 0,
              centerY: 1,
              visible: true,
            });

            if (showDowntimeTooltip) {
              tooltip.show();
            }
          });

          axisFill.events.on('pointerout', () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = window.setTimeout(() => {
              try {
                if (tooltip && !tooltip.isDisposed?.()) {
                  tooltip.hide();
                }
              } catch (error) {
                console.warn('Error hiding tooltip:', error);
              }
            }, 300);
          });

          tooltip.events.on('pointerover', () => {
            if (hideTimeout) {
              clearTimeout(hideTimeout);
              hideTimeout = null;
            }
          });

          tooltip.events.on('pointerout', () => {
            if (hideTimeout) clearTimeout(hideTimeout);
            hideTimeout = window.setTimeout(() => {
              try {
                if (tooltip && !tooltip.isDisposed?.()) {
                  tooltip.hide();
                }
              } catch (error) {
                console.warn('Error hiding tooltip:', error);
              }
            }, 300);
          });

          axisFill.set('y', -20);
        }

        axisRange.get('grid')?.set('visible', false);
      });
    }
  });
}

export function syncCursor(amCharRefs: React.MutableRefObject<(XYChart | null)[]>) {
  const chartsToSync = (amCharRefs.current as XYChart[]).filter((chart) => chart !== null);

  am5.array.each(chartsToSync, function (sourceChart: any) {
    const syncCursors: any = [];
    am5.array.each(chartsToSync, function (targetChart: any) {
      if (targetChart !== sourceChart) {
        syncCursors.push(targetChart?.get('cursor'));
      }
    });
    sourceChart?.get('cursor')?.set('syncWith', syncCursors, { axis: sourceChart.xAxes.getIndex(0) });
  });
}

export function syncAxes(amCharRefs: React.MutableRefObject<(XYChart | null)[]>, targetChart: any) {
  // Check if targetChart exists and has xAxes before proceeding
  if (!targetChart || !targetChart.xAxes) {
    return;
  }

  // Additional safety check for disposed chart
  try {
    if (targetChart.isDisposed?.() === true) {
      return;
    }
  } catch (e) {
    // If isDisposed method fails, assume chart is disposed
    return;
  }

  const chartsToSync = (amCharRefs.current as XYChart[]).filter((chart) => {
    if (!chart || !chart.xAxes) return false;
    try {
      return chart.isDisposed?.() !== true;
    } catch (e) {
      return false;
    }
  });

  let targetAxis;
  try {
    targetAxis = targetChart.xAxes.getIndex(0);
  } catch (e) {
    // Chart might be disposed while accessing xAxes
    return;
  }

  // Check if targetAxis exists before proceeding
  if (!targetAxis) {
    return;
  }

  // // Skip sync if the target chart is marked as receiver only (e.g., Contextualizer)
  // // unless it explicitly allows zoom sync (e.g., during zoom reset)
  // if (targetChart && '_isReceiverOnly' in targetChart && targetChart._isReceiverOnly === true) {
  //   // Allow sync if the chart has _allowZoomSync flag set to true
  //   if (!('_allowZoomSync' in targetChart) || !targetChart._allowZoomSync) {
  //     return;
  //   }
  // }

  const start = targetAxis.get('start');
  const end = targetAxis.get('end');
  const zoom = targetAxis.zoom;

  am5.array.each(chartsToSync, function (chart) {
    if (chart !== targetChart && chart.xAxes) {
      try {
        const axis: any = chart.xAxes.getIndex(0);

        // Synchronize both zoom level and visible range
        if (zoom !== undefined) {
          axis.zoom = zoom;
        }

        axis.setAll({
          start: start,
          end: end,
        });
      } catch (e) {
        // Skip this chart if there's an error accessing its axis
        console.warn('Error syncing axis for chart:', e);
      }
    }
  });
}

export function updateYAxisRange(allSeries: am5xy.LineSeries[], yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>) {
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  allSeries.forEach((series) => {
    if (series.get('visible')) {
      series.dataItems.forEach((dataItem) => {
        const value = dataItem.get('valueY');
        if (typeof value === 'number' && !isNaN(value)) {
          if (value < minY) minY = value;
          if (value > maxY) maxY = value;
        }
      });
    }
  });

  // Only set if we have valid min/max
  if (minY !== Number.POSITIVE_INFINITY && maxY !== Number.NEGATIVE_INFINITY) {
    // Handle case where all values are the same (like all zeros)
    if (minY === maxY) {
      const range = Math.abs(minY) || 1;
      yAxis.setAll({
        min: minY - range * 0.1,
        max: maxY + range * 0.1,
        strictMinMax: false,
      });
    } else {
      yAxis.set('min', minY);
      yAxis.set('max', maxY);
    }
  } else {
    // Reset to auto if nothing is visible
    yAxis.set('min', undefined);
    yAxis.set('max', undefined);
  }
}

export const createChartTooltip = (
  chart: am5xy.XYChart,
  root: am5.Root,
  seriesConfig: Array<{
    name: string;
    field: string;
    tooltipLabel: string;
    units: string;
    color?: string;
    testColor?: string;
  }>,
  isOilGasChart = false,
) => {
  const tooltip = createTooltip('', root, {
    backgroundColor: '#001023',
    backgroundOpacity: 1,
    strokeColor: '#001023',
    textColor: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'Mulish',
    textAlign: 'left',
  });

  tooltip.set('pointerOrientation', 'left');

  // Add dynamic positioning adapter for OilGasChart
  if (isOilGasChart) {
    // Shared calculation function for both adapters
    const calculateDynamicPosition = (target: any): { dx: number; pointerOrientation: 'left' | 'right' } => {
      const dataItem = target.dataItem;
      if (!dataItem) return { dx: 20, pointerOrientation: 'left' };

      const series = dataItem.component as am5xy.LineSeries;
      if (!series) return { dx: 20, pointerOrientation: 'left' };

      const chart = series.chart;
      if (!chart) return { dx: 20, pointerOrientation: 'left' };

      // Get the x-axis to calculate actual position
      const xAxis = series.get('xAxis') as am5xy.DateAxis<am5xy.AxisRenderer>;
      if (!xAxis) return { dx: 20, pointerOrientation: 'left' };

      // Get the actual x value from the data item
      const valueX = dataItem.get('valueX');
      if (typeof valueX !== 'number') return { dx: 20, pointerOrientation: 'left' };

      // Calculate the actual x coordinate in pixels
      const xPosition = xAxis.valueToPosition(valueX);
      const chartWidth = chart.plotContainer.width();
      const xCoord = chartWidth * xPosition;

      const minGap = 20;
      const padding = 10;

      // Get actual tooltip width from label (more accurate)
      let tooltipWidth = 180; // Conservative default estimate
      if (target.label) {
        // Try to get the actual width
        const measuredWidth = target.label.width();
        if (typeof measuredWidth === 'number' && measuredWidth > 0) {
          tooltipWidth = measuredWidth + 30; // Add padding
        }
      }

      // Check available space on right (be conservative - add buffer)
      const rightPosition = xCoord + minGap;
      const spaceOnRight = chartWidth - rightPosition - padding;
      const requiredSpace = tooltipWidth + 10; // Add small buffer

      // Only flip to left if there's clearly not enough space on right
      if (spaceOnRight >= requiredSpace) {
        // Enough space on right - prefer right side
        return { dx: minGap, pointerOrientation: 'left' };
      } else {
        // Not enough space on right, check if we can position on left
        const leftPosition = xCoord - minGap - tooltipWidth;
        const spaceOnLeft = leftPosition - padding;

        // Only flip to left if there's enough space on left AND not enough on right
        if (spaceOnLeft >= 0 && spaceOnRight < requiredSpace) {
          return { dx: -minGap, pointerOrientation: 'right' };
        } else {
          // Not enough space on either side, but prefer right with minimal gap
          // Only use left if we're very close to right edge
          if (xCoord > chartWidth * 0.85) {
            // Very close to right edge (85%+), position on left
            const leftX = Math.max(padding, xCoord - tooltipWidth - padding);
            return { dx: leftX - xCoord, pointerOrientation: 'right' };
          } else {
            // Still prefer right side, just reduce gap if needed
            const maxRightX = chartWidth - tooltipWidth - padding;
            const rightX = Math.min(maxRightX, xCoord + minGap);
            return { dx: rightX - xCoord, pointerOrientation: 'left' };
          }
        }
      }
    };

    // Adapter for dx
    tooltip.adapters.add('dx', (_dx, target) => {
      const result = calculateDynamicPosition(target);
      return result.dx;
    });

    // Adapter for pointerOrientation
    tooltip.adapters.add('pointerOrientation', (_orientation, target) => {
      const result = calculateDynamicPosition(target);
      return result.pointerOrientation;
    });
  }

  tooltip.adapters.add('labelText', (_text, target) => {
    const dataItem = target.dataItem;
    if (!dataItem) return '';

    const dataContext = dataItem.dataContext as any;
    if (!dataContext) return '';

    let tooltipContent = `[bold]{valueX.formatDate('MMM d, yyyy hh:mm a')}[/]`;

    chart.series.each((series) => {
      if (series.get('visible') === false) return;

      const config = seriesConfig.find((config) => config.name === series.get('name'));
      if (config) {
        const value = dataContext[config.field];
        if (value !== undefined && value !== null && value !== '') {
          const units = config.units ? ` ${config.units}` : '';
          const color = isOilGasChart ? config.testColor : config.color;
          // Format Total Liquids to 2 decimal places
          const formattedValue = config.field === 'TotalFluid' ? Number(value).toFixed(2) : value;
          tooltipContent += `\n[${color}]●[/] ${config.tooltipLabel}: ${formattedValue}${units}`;
        }
      }
    });

    return tooltipContent;
  });

  return tooltip;
};

export function createCustomMarkerLegend(
  chart: am5.Container,
  root: am5.Root,
  legendSeries: am5xy.LineSeries[],
  seriesConfig: Array<{ name: string; color: string; shape?: string }>,
  customPadding?: boolean,
) {
  const legend = chart.children.push(
    am5.Legend.new(root, {
      x: am5.p50,
      centerX: am5.p50,
      paddingLeft: 80,
      marginTop: 30,
      useDefaultMarker: false,
    }),
  );

  legend.labels.template.setAll({
    fontSize: 12,
    fontFamily: 'Mulish',
    fontWeight: '500',
  });
  legend.markers.template.setAll({ width: 32, height: 16 });

  const emptyDataColor = am5.color('#8a8f98');
  const inactiveColor = am5.color('#F7F9F9');
  const transparent = am5.color('#131f2e');

  const refreshLegendMarker = (dataItem: any) => {
    const series = dataItem?.dataContext as am5xy.LineSeries;
    const marker = dataItem?.get('marker') as am5.Container;
    const label = dataItem?.get('label');
    if (!series || !marker || !label) return;

    const cfg = seriesConfig.find((c) => c.name === series.get('name'));
    if (!cfg) return;

    const baseColor = am5.color(cfg.color);
    const shape = cfg.shape;
    const isVisible = series.get('visible') !== false;
    const hasNoData = !series.data || series.data.length === 0;

    const strokeColor = hasNoData ? emptyDataColor : isVisible ? baseColor : inactiveColor;
    const strokeOpacity = hasNoData ? 0.4 : isVisible ? 1 : 0.8;
    const fillColor = isVisible && !hasNoData ? baseColor : transparent;

    label.set('opacity', strokeOpacity);

    marker.children.clear();

    if (shape) {
      marker.children.push(
        am5.Graphics.new(root, {
          width: 32,
          height: 2,
          x: 0,
          y: 7,
          stroke: strokeColor,
          strokeWidth: 2,
          strokeOpacity,
          draw: (display: any) => {
            display.moveTo(0, 1);
            display.lineTo(32, 1);
          },
        }),
      );

      const shapeMarker = generateMarkers(shape, cfg.color, 3, root);
      shapeMarker.setAll({
        x: 16,
        y: 8,
        fill: fillColor,
        fillOpacity: fillColor === transparent ? 0 : 1,
        stroke: strokeColor,
        strokeWidth: 2,
        strokeOpacity,
      });
      marker.children.push(shapeMarker);
    } else {
      marker.children.push(
        am5.Circle.new(root, {
          radius: 6,
          x: 16,
          y: 8,
          centerX: am5.p50,
          centerY: am5.p50,
          fill: fillColor,
          fillOpacity: fillColor === transparent ? 0 : 1,
          stroke: strokeColor,
          strokeWidth: 2,
          strokeOpacity,
        }),
      );
    }
  };

  if (customPadding) {
    legend.setAll({
      paddingLeft: 0,
      paddingRight: 0,
    });
  }

  // Initial markers
  legend.data.setAll(legendSeries);
  legend.dataItems.forEach((di) => refreshLegendMarker(di));

  // Refresh on visibility toggle
  legend.dataItems.forEach((di) => {
    const series = di.dataContext as am5xy.LineSeries;
    series.on('visible', () => refreshLegendMarker(di));
  });

  return legend;
}
